import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaUserTie } from 'react-icons/fa';
import { BiLoaderAlt } from 'react-icons/bi';
import { IoMdClose } from 'react-icons/io';
import { API_ENDPOINTS } from '../apiConfig';

const CandidateRemarks = ({ candidateId, candidateName, onClose }) => {
  const [remarks, setRemarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    interviewerName: '',
    remarkText: '',
    date: new Date().toISOString().split('T')[0],
    salaryExpectation: '',
    preferredLocation: ''
  });

  const fetchRemarks = useCallback(async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.GET_REMARKS(candidateId));
      setRemarks(response.data);
    } catch (error) {
      toast.error('Error fetching remarks: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => {
    fetchRemarks();
  }, [fetchRemarks]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(API_ENDPOINTS.ADD_REMARK, {
        candidateId,
        ...formData,
        salaryExpectation: Number(formData.salaryExpectation)
      });
      
      toast.success('Remark added successfully!');
      setFormData({
        interviewerName: '',
        remarkText: '',
        date: new Date().toISOString().split('T')[0],
        salaryExpectation: '',
        preferredLocation: ''
      });
      setShowForm(false);
      fetchRemarks();
    } catch (error) {
      toast.error('Error adding remark: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <BiLoaderAlt className="animate-spin text-2xl text-[#fbba00]" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 space-y-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <FaUserTie className="text-[#fbba00] text-xl" />
              <h2 className="text-xl font-semibold text-[#080366]">
                Remarks 
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <IoMdClose className="text-2xl" />
            </button>
          </div>

          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 bg-[#fbba00] hover:bg-[#f18738] text-white px-4 py-2 rounded-lg transition-colors"
          >
            {showForm ? 'Cancel' : 'Add New Remark'}
          </button>

          {showForm && (
            <form onSubmit={handleSubmit} className="space-y-4 bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Interviewer Name
                  </label>
                  <input
                    type="text"
                    name="interviewerName"
                    value={formData.interviewerName}
                    onChange={handleChange}
                    required
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#fbba00] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#fbba00] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Salary Expectation
                  </label>
                  <input
                    type="number"
                    name="salaryExpectation"
                    value={formData.salaryExpectation}
                    onChange={handleChange}
                    required
                    placeholder="Enter amount"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#fbba00] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preferred Location
                  </label>
                  <input
                    type="text"
                    name="preferredLocation"
                    value={formData.preferredLocation}
                    onChange={handleChange}
                    required
                    placeholder="Enter location"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#fbba00] focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Remark
                </label>
                <textarea
                  name="remarkText"
                  value={formData.remarkText}
                  onChange={handleChange}
                  required
                  rows="3"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#fbba00] focus:border-transparent"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#080366] hover:bg-[#060244] text-white py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <BiLoaderAlt className="animate-spin" />
                ) : (
                  'Submit Remark'
                )}
              </button>
            </form>
          )}

          <div className="space-y-4">
            {remarks.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No remarks available</p>
            ) : (
              remarks.map((remark) => (
                <div
                  key={remark._id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <FaUserTie className="text-[#fbba00]" />
                      <span className="font-medium">{remark.interviewerName}</span>
                    </div>
                    <span className="text-sm text-gray-500">
                      {new Date(remark.date).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="mt-2 text-gray-700 whitespace-pre-wrap">
                    {remark.remarkText}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-600">
                    <span>Salary Expectation: ₹{remark.salaryExpectation.toLocaleString()}</span>
                    <span>Preferred Location: {remark.preferredLocation}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateRemarks;